/**
 * Rebuild Kaching option-name bar on PDP from option controls' `data-name` attributes.
 *
 * Why: Kaching's own `.kaching-bundles__bar-variant-names` is `display:none` on PDP,
 * but the option controls exist and expose the labels via `data-name`.
 *
 * Strategy:
 * - For each product card in the bundles bar (non-modal), read unique `data-name`s
 * - Render them into our own visible bar `.tbw-kaching-variant-names`
 * - Keep it updated on re-renders (MutationObserver, debounced, loop-safe)
 */
(function () {
  // Prevent double-init (theme editors / app reloads can execute assets more than once).
  if (window.__TBW_KACHING_VARIANT_NAMES_INIT) return;
  window.__TBW_KACHING_VARIANT_NAMES_INIT = true;

  const TBW_BAR_CLASS = "tbw-kaching-variant-names";
  const TBW_BAR_SEL = `.${TBW_BAR_CLASS}`;

  const CONTENT_SEL = ".kaching-bundles__collection-product__content";
  const SELECTS_SEL = ".kaching-bundles__bar-variant-selects";
  const OPTION_SEL = ".kaching-bundles-option[data-name]";
  const VARIANT_SEL = ".kaching-bundles__bar-variant";
  const MODAL_ROOT_SEL = ".kaching-bundles__choose-product";

  const DEBUG = !!window.TBW_KACHING_VARIANT_NAMES_DEBUG;
  const log = (...args) => {
    if (DEBUG) console.log("[tbw-kaching-variant-names]", ...args);
  };

  const uniq = (arr) => {
    const seen = new Set();
    const out = [];
    for (const s of arr) {
      const v = (s || "").trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
    return out;
  };

  const buildForContent = (contentEl) => {
    // Never touch modal UIs (they already show the bar)
    if (contentEl.closest(MODAL_ROOT_SEL)) return false;

    const selects = contentEl.querySelector(SELECTS_SEL);
    const variant = contentEl.querySelector(VARIANT_SEL);
    if (!selects || !variant) return false;

    const names = uniq(
      Array.from(selects.querySelectorAll(OPTION_SEL)).map((el) =>
        el.getAttribute("data-name")
      )
    );
    if (!names.length) return false;

    let bar = contentEl.querySelector(TBW_BAR_SEL);
    if (!bar) {
      bar = document.createElement("div");
      bar.className = TBW_BAR_CLASS;
      variant.insertAdjacentElement("beforebegin", bar);
      log("inserted", bar);
    }

    const sig = names.join(" | ");
    if (bar.getAttribute("data-names-sig") === sig) return true;

    bar.setAttribute("data-names-sig", sig);
    bar.replaceChildren(
      ...names.map((n) => {
        const span = document.createElement("span");
        span.textContent = n;
        return span;
      })
    );
    log("updated", { sig });
    return true;
  };

  const renderAll = () => {
    const contents = document.querySelectorAll(CONTENT_SEL);
    if (!contents.length) return 0;
    let touched = 0;
    contents.forEach((c) => {
      if (buildForContent(c)) touched++;
    });
    return touched;
  };

  const init = () => {
    // When this runs via a <script> in <head>, `document.body` may not exist yet.
    if (!document.body) return false;

    // Run once on init (Kaching may mount after, observer handles that too)
    const initialTouched = renderAll();
    log("initial", { touched: initialTouched });

    // Prefer observing the Kaching root to avoid reacting to unrelated page mutations.
    const getPreferredRoot = () =>
      document.querySelector(".kaching-bundles") ||
      document.querySelector("kaching-bundle") ||
      document.querySelector("kaching-bundles-block") ||
      document.body;

    let currentRoot = null;
    let scheduled = false;

    const schedule = (reason) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;

        // Rebind to a more specific root once the app is present / if it was replaced.
        const nextRoot = getPreferredRoot();
        if (nextRoot && nextRoot !== currentRoot) {
          obs.disconnect();
          currentRoot = nextRoot;
          obs.observe(currentRoot, { childList: true, subtree: true });
          log("observing", currentRoot);
        }

        // Temporarily disconnect while we mutate to avoid feedback loops.
        obs.disconnect();
        const touched = renderAll();
        log(reason, { touched });
        // Reconnect (root may have changed during renderAll).
        currentRoot = getPreferredRoot();
        obs.observe(currentRoot, { childList: true, subtree: true });
      });
    };

    const obs = new MutationObserver((muts) => {
      // Ignore our own bar updates
      for (const m of muts) {
        const t = m.target;
        if (t && t.nodeType === 1 && t.closest && t.closest(TBW_BAR_SEL)) return;
      }

      schedule("mutation");
    });

    // Start observing immediately; if Kaching isn't mounted yet we'll be observing `body`.
    currentRoot = getPreferredRoot();
    obs.observe(currentRoot, { childList: true, subtree: true });
    log("observing", currentRoot);

    // Belt-and-braces: some UI toggles may not trigger childList mutations we can see.
    // Any interaction inside the Kaching app schedules a refresh.
    const interactionRootSelector = ".kaching-bundles, kaching-bundle, kaching-bundles-block";
    const onInteraction = (e) => {
      const t = e && e.target;
      if (!t || !t.closest) return;
      if (!t.closest(interactionRootSelector)) return;
      schedule(e.type);
    };
    document.addEventListener("click", onInteraction, true);
    document.addEventListener("change", onInteraction, true);

    // Expose a manual refresh hook for debugging
    window.TBW_KachingVariantNamesRefresh = () => renderAll();
    return true;
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        if (init()) return;
        // Extremely defensive: if `body` still isn't ready (shouldn't happen), try again shortly.
        window.setTimeout(init, 50);
      },
      { once: true }
    );
  } else {
    if (!init()) window.setTimeout(init, 0);
  }
})();
